import React, { useState, useEffect } from 'react';
import { Box } from '@mui/material';
import BlogCard from '../cards/blogCard/blogCard';
import IconButton from '../icon_button/iconButton';
import Button from '../button/button';
import { blogCardSliderPropTypes } from './BlogCardSlider.propTypes';

/**
 * BlogCardSlider component for displaying a list of blog cards in a slider format.
 * The component supports sliding animations, shaking effects, and customizable transition speeds.
 * Users can navigate through the blog cards using previous and next buttons.
 *
 * @param {Object} props - The component props.
 * @param {Array} props.blogCardList - List of blog cards to display, each item being an object with properties `title`, `content`, `image`, `imageAlt`, and `onClick`.
 * @param {number} [props.slideSpeed=0.75] - Speed of the sliding transition in seconds.
 * @param {number} [props.shakeDuration=0.3] - Duration of the shaking effect in seconds after a slide transition.
 * @param {number} [props.elevation=0] - Elevation level of the blog cards (Material-UI elevation prop).
 * @returns {JSX.Element} The rendered BlogCardSlider component.
 */
export default function BlogCardSlider({
  blogCardList,
  slideSpeed = 0.75,
  shakeDuration = 0.3,
  elevation = 0,
  prefetch,
}) {
  const [cardIndex, setCardIndex] = useState(0);
  const [shake, setShake] = useState(false);
  const [transitioning, setTransitioning] = useState(false);

  const handlePrevClick = () => {
    setCardIndex(prevIndex =>
      prevIndex === 0 ? blogCardList.length - 1 : prevIndex - 1,
    );
    setTransitioning(true);
  };

  const handleNextClick = () => {
    setCardIndex(prevIndex => (prevIndex + 1) % blogCardList.length);
    setTransitioning(true);
  };

  useEffect(() => {
    if (transitioning) {
      const shakeStartDelay = slideSpeed * 1000;
      const timer = setTimeout(() => {
        setShake(true);
        setTimeout(() => {
          setShake(false);
          setTransitioning(false);
        }, shakeDuration * 1000);
      }, shakeStartDelay);

      return () => clearTimeout(timer);
    }
  }, [transitioning, slideSpeed, shakeDuration]);

  return (
    <>
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          maxWidth: '400px',
          overflow: 'hidden',
          margin: '0 auto',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            transition: `transform ${slideSpeed}s ease-in-out`,
            transform: `translateX(-${cardIndex * 100}%)`,
            width: '100%',
          }}
        >
          {blogCardList.map((blog, index) => (
            <Box
              key={index}
              sx={{
                flex: '0 0 100%',
                boxSizing: 'border-box',
                pb: '20px',
                px: '5px',
                '@keyframes shake': {
                  '0%': { transform: 'translateX(0) translateY(0) ' },
                  '25%': {
                    transform: 'translateX(1px) translateY(-1px) ',
                  },
                  '50%': {
                    transform: 'translateX(-1px) translateY(1px) ',
                  },
                  '75%': {
                    transform: 'translateX(1px) translateY(-1px) ',
                  },
                  '100%': {
                    transform: 'translateX(0) translateY(0) ',
                  },
                },
                animation: shake ? `shake ${shakeDuration}s` : 'none',
              }}
            >
              <BlogCard
                title={blog.title}
                content={blog.content}
                image={blog.image}
                imageAlt={blog.imageAlt}
                elevation={elevation}
                backgroundColor="transparent"
                action={
                  <Button
                    variant="contained"
                    size="large"
                    color="secondary"
                    fontFamily="body.fontFamily.secondary"
                    fontWeight={700}
                    href={blog.href}
                    onClick={blog.onClick}
                    prefetch={prefetch}
                    sx={{ mt: 3, py: 2, px: 3, position: 'relative' }}
                  >
                    {blog.buttonLabel || 'Read More'}
                    <span className="sr-only">
                      {blog.buttonLabel 
                        ? `${blog.buttonLabel} - ${blog.title || blog.cardTitle || 'featured content'}`
                        : `Read more about ${blog.title || blog.cardTitle || 'this featured content'}`
                      }
                    </span>
                  </Button>
                }
              />
            </Box>
          ))}
        </Box>
      </Box>
      <Box sx={{ display: 'flex' }}>
        <IconButton
          backgroundColor="secondary"
          color="#ffffff"
          hasBackground
          height="72px"
          onClick={handlePrevClick}
          shape="square"
          size="medium"
          type="backarrow"
          sx={{
            mr: 0.5,
            flexBasis: '50%',
            maxWidth: '50vw',
            '&:hover': { backgroundColor: 'primary.main' },
            '&:active': { backgroundColor: 'primary.main' },
          }}
        />
        <IconButton
          backgroundColor="secondary"
          color="#ffffff"
          hasBackground
          height="72px"
          onClick={handleNextClick}
          shape="square"
          size="medium"
          type="forwardarrow"
          sx={{
            flexBasis: '50%',
            maxWidth: '48vw',
            '&:hover': { backgroundColor: 'primary.main' },
            '&:active': { backgroundColor: 'primary.main' },
          }}
        />
      </Box>
    </>
  );
}

BlogCardSlider.propTypes = blogCardSliderPropTypes;
